import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";

function CategoryColumn({ title, type, items, onChanged, testPrefix }) {
  const [newName, setNewName] = useState("");

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/categories", { name: newName.trim(), type });
      toast.success(`${title.slice(0, -1)} added`);
      setNewName("");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove "${name}"? Products already in it keep their label.`)) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Removed");
      onChanged();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="border border-white/10 bg-surface p-4" data-testid={`${testPrefix}-column`}>
      <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">{title}</h3>
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={`New ${title.slice(0, -1).toLowerCase()}...`}
          className="flex-1 border border-white/10 bg-obsidian px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
          data-testid={`${testPrefix}-input`}
        />
        <button onClick={add} className="flex items-center gap-1 bg-volt px-4 py-2 font-mono text-xs font-bold uppercase text-obsidian hover:bg-white" data-testid={`${testPrefix}-add`}>
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between border border-white/10 bg-obsidian px-3 py-2" data-testid={`${testPrefix}-item-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="font-mono text-sm text-slate-300">{c.name}</span>
            <button onClick={() => remove(c.id, c.name)} className="p-1 text-slate-500 hover:text-red-400" data-testid={`${testPrefix}-delete-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="font-mono text-xs text-slate-600">None yet</p>}
      </div>
    </div>
  );
}

export function AdminCategories() {
  const [cats, setCats] = useState([]);

  const load = () => api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="categories-manager">
      <p className="mb-4 text-sm text-slate-400">Item categories appear in the shop filter and product form. Bike categories are the fitment options buyers see.</p>
      <div className="grid gap-6 md:grid-cols-2">
        <CategoryColumn title="Item Categories" type="item" items={cats.filter((c) => c.type === "item")} onChanged={load} testPrefix="item-cat" />
        <CategoryColumn title="Bike Categories" type="bike" items={cats.filter((c) => c.type === "bike")} onChanged={load} testPrefix="bike-cat" />
      </div>
    </div>
  );
}
